import { BodyContentDetails } from '@modules/front-office/interface/voucher.interface';
import { SxProps } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useAppSelector } from '@store/index';
import { setTemplate } from '@store/slices/pdf/react-pdf.slice';
import PDFDocument, { measureTemplate } from '../pdf-document';
import {
  Cell,
  Field,
  HeaderFooterType,
  ImageField,
  PageSize,
  PageType,
  Row,
  Section,
  SelectedElement,
  SharedCellStyle,
  TableCell,
  TableField,
  TableRow,
  TargetType,
  TemplateState,
  TextAlignmentType,
} from '../react-pdf.interface';
import { selectSelectedPresent } from '@store/selector/react-pdf.selector';

const TAG_DETECT_REGEX = /\{([^}]+)\}/;

const TAG_REPLACE_REGEX = /\{([^}]+)\}/g;

const getPlaceholders = (value: string): string[] => {
  const matches = value.match(/{([^}]+)}/g);

  if (!matches) return [];

  return matches.map((item) => item.slice(1, -1));
};

export const hasDynamicTag = (value: string) => TAG_DETECT_REGEX.test(value);

export const injectDynamicData = (
  template: string,
  data: Record<string, string | number>,
): string =>
  template.replace(TAG_REPLACE_REGEX, (_, key) =>
    data[key] !== undefined ? String(data[key]) : '',
  );

export const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

export const mapPageStyleToSx = (style?: PageType['style']): SxProps => {
  if (!style) return {};
  const {
    // rowGap,
    alignItems,
    justifyContent,
    backgroundColor,
    padding,
    border,
  } = style;

  return {
    // ...(rowGap && { rowGap: rowGap + 'px' }),
    ...(alignItems && { alignItems }),
    ...(justifyContent && { justifyContent }),
    ...(backgroundColor && { backgroundColor }),

    ...(padding && {
      pt: padding.top + 'px',
      pr: padding.right + 'px',
      pb: padding.bottom + 'px',
      pl: padding.left + 'px',
    }),

    ...(border && {
      borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
    }),
  };
};

export const mapSectionStyleToSx = (style?: Section['style']): SxProps => {
  if (!style) return {};
  const {
    width,
    alignItems,
    justifyContent,
    backgroundColor,
    padding,
    border,
    rowGap,
  } = style;

  return {
    ...(width && { width: width + '%' }),
    ...(rowGap && { rowGap: rowGap + 'px' }),
    ...(alignItems && { alignItems }),
    ...(justifyContent && { justifyContent }),
    ...(backgroundColor && { backgroundColor }),

    ...(padding && {
      pt: padding.top + 'px',
      pr: padding.right + 'px',
      pb: padding.bottom + 'px',
      pl: padding.left + 'px',
    }),

    ...(border && {
      borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
    }),
  };
};

export const mapRowStyleToSx = (style?: Row['style']): SxProps => {
  if (!style) return {};
  const {
    width,
    alignItems,
    justifyContent,
    backgroundColor,
    padding,
    border,
    columnGap,
    flexDirection,
  } = style;

  return {
    ...(width && { width: width + '%' }),
    ...(columnGap && { columnGap: columnGap + 'px' }),
    ...(alignItems && { alignItems }),
    ...(justifyContent && { justifyContent }),
    ...(backgroundColor && { backgroundColor }),
    ...(flexDirection && { flexDirection }),

    ...(padding && {
      pt: padding.top + 'px',
      pr: padding.right + 'px',
      pb: padding.bottom + 'px',
      pl: padding.left + 'px',
    }),

    ...(border && {
      borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
    }),
  };
};

export const mapCellStyleToSx = (style?: Cell['style']): SxProps => {
  if (!style) return {};
  const {
    width,
    alignItems,
    justifyContent,
    backgroundColor,
    padding,
    border,
    flexDirection,
    gap,
  } = style;

  return {
    ...(width && {
      width: width + '%',
    }),
    ...(alignItems && { alignItems }),
    ...(justifyContent && { justifyContent }),
    ...(backgroundColor && { backgroundColor }),
    ...(flexDirection && { flexDirection }),
    ...(gap && { gap: gap + 'px' }),
    ...(padding && {
      pt: padding.top + 'px',
      pr: padding.right + 'px',
      pb: padding.bottom + 'px',
      pl: padding.left + 'px',
    }),

    ...(border && {
      borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
    }),
  };
};

export const mapFieldStyleToSx = (field?: Field): SxProps => {
  if (!field || !field.style) return {};

  switch (field.type) {
    case 'title':
    case 'textarea': {
      const {
        color,
        fontFamily,
        fontSize,
        fontStyle,
        fontWeight,
        letterSpacing,
        lineHeight,
        padding,
        textAlign,
        textDecoration,
        textTransform,
        wordSpacing,
        width,
        border,
        backgroundColor,
      } = field.style;
      return {
        ...(width && { width: width + '%' }),
        ...(fontFamily && { fontFamily: fontFamily + '!important' }),
        ...(fontSize && { fontSize: fontSize + 'px!important' }),
        ...(fontWeight && { fontWeight: fontWeight + '!important' }),
        ...(textTransform && { textTransform }),
        ...(color && { color: color }),
        ...(textDecoration && { textDecoration }),
        ...(fontStyle && { fontStyle }),
        ...(letterSpacing && { letterSpacing: letterSpacing + 'px' }),
        ...(wordSpacing && { wordSpacing: wordSpacing + 'px' }),
        ...(lineHeight && { lineHeight }),
        ...(textAlign && { textAlign }),
        ...(padding && {
          pt: padding.top + 'px',
          pr: padding.right + 'px',
          pb: padding.bottom + 'px',
          pl: padding.left + 'px',
        }),
        ...(border && {
          borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
          borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
          borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
          borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
        }),
        ...(backgroundColor && { backgroundColor }),
      };
    }

    case 'image': {
      const { height, objectFit, objectPosition, padding, width } = field.style;
      return {
        width: width || '0' + 'px',
        height: height || '0' + 'px',
        ...(objectFit && { objectFit }),
        ...(objectPosition && { objectPosition }),

        ...(padding && {
          pt: padding.top + 'px',
          pr: padding.right + 'px',
          pb: padding.bottom + 'px',
          pl: padding.left + 'px',
        }),
      };
    }

    case 'table': {
      const { padding, width, backgroundColor, border } = field.style;
      return {
        ...(width && {
          width: width + '%',
        }),
        ...(backgroundColor && { backgroundColor }),

        ...(padding && {
          pt: padding.top + 'px',
          pr: padding.right + 'px',
          pb: padding.bottom + 'px',
          pl: padding.left + 'px',
        }),
        ...(border && {
          borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
          borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
          borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
          borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
        }),
      };
    }

    default:
      return {};
  }
};

export const mapTableStyleToSx = (style?: SharedCellStyle): SxProps => {
  if (!style) return {};

  const {
    backgroundColor,
    border,
    color,
    fontFamily,
    fontSize,
    fontStyle,
    fontWeight,
    letterSpacing,
    lineHeight,
    padding,
    textAlign,
    textDecoration,
    textTransform,
    wordSpacing,
    width,
  } = style;

  return {
    ...(width && { width: width + '%' }),
    ...(fontFamily && { fontFamily }),
    ...(fontSize && { fontSize: fontSize + 'px' }),
    ...(fontWeight && { fontWeight }),
    ...(textTransform && { textTransform }),
    ...(color && { color: color }),
    ...(textDecoration && { textDecoration }),
    ...(fontStyle && { fontStyle }),
    ...(letterSpacing && { letterSpacing: letterSpacing + 'px' }),
    ...(wordSpacing && { wordSpacing: wordSpacing + 'px' }),
    ...(lineHeight && { lineHeight }),
    ...(textAlign && { textAlign }),
    ...(backgroundColor && { backgroundColor }),
    ...(padding && {
      pt: padding.top + 'px',
      pr: padding.right + 'px',
      pb: padding.bottom + 'px',
      pl: padding.left + 'px',
    }),
    ...(border && {
      borderTop: `${border.width?.top ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderRight: `${border.width?.right ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderBottom: `${border.width?.bottom ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
      borderLeft: `${border.width?.left ?? 0}px ${border.style ?? 'solid'} ${border.color ?? '#000000'}`,
    }),
  };
};

export const getFieldWidth = (field?: Field): string | undefined => {
  if (!field || !field.style || (!field.style.width && field.type !== 'image'))
    return undefined;

  if (field.type === 'image') {
    if (!field.style.width) {
      return '0px';
    }
    return typeof field.style.width === 'number'
      ? `${field.style.width}px`
      : field.style.width;
  }

  return typeof field.style.width === 'number'
    ? `${field.style.width}%`
    : field.style.width;
};

export const getFieldHeight = (field?: ImageField): string | undefined => {
  if (!field || !field.style || !field.style.height) return undefined;

  return typeof field.style.height === 'number'
    ? `${field.style.height}px`
    : field.style.height;
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('FileReader result was not a string'));
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file); // triggers reader.onload
  });
};

export const base64ToBlob = (base64: string, mimeType = ''): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays: BlobPart[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays as BlobPart[], { type: mimeType });
};

export const base64ToFile = (
  base64: string,
  filename: string,
  mimeType = '',
): File => {
  const blob = base64ToBlob(base64, mimeType);
  return new File([blob], filename, { type: mimeType });
};

export const downloadTemplate = (
  template: TemplateState,
  fileName = 'template.json',
) => {
  const dataStr =
    'data:text/json;charset=utf-8,' +
    encodeURIComponent(JSON.stringify(template, null, 2));
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', dataStr);
  downloadAnchorNode.setAttribute('download', fileName);
  document.body.appendChild(downloadAnchorNode); // required for firefox
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

export const isTemplateState = (data: any): data is TemplateState => {
  return (
    data &&
    typeof data === 'object' &&
    Array.isArray(data.pages) &&
    data.pages.every(
      (page: any) =>
        page &&
        typeof page === 'object' &&
        Array.isArray(page.sections) &&
        page.sections.every(
          (section: any) =>
            section &&
            typeof section === 'object' &&
            Array.isArray(section.rows),
        ),
    )
  );
};

export const importTemplate = (
  event: React.ChangeEvent<HTMLInputElement>,
  dispatch: any,
  isHaveId?: boolean,
) => {
  const file = event.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const template = JSON.parse(e.target?.result as string);
      if (!isTemplateState(template)) {
        alert('Invalid template format. Please upload a valid template JSON.');
        throw new Error('Invalid template format');
      }
      dispatch(
        setTemplate({
          ...template,
          config: {
            enableDesigner: true,
            hideIdTagging: isHaveId,
          },
        }),
      );
    } catch (error) {
      console.error('Failed to parse template:', error);
    }
  };
  reader.readAsText(file);
};

export const downloadPdf = async (template: TemplateState) => {
  const newTemplate = await measureTemplate(template);
  const blob = await pdf(<PDFDocument template={newTemplate} />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'template.pdf';
  link.click();
  URL.revokeObjectURL(url);
};

// #region SLICE HELPER
export const selectSections = (
  template: TemplateState,
  index?: {
    pageIndex: number;
    sectionIndex: number;
    target?: TargetType;
  },
  dynamic?: boolean,
): Section | HeaderFooterType | undefined => {
  const { selectedElement, pages, footer = [], header = [] } = template;
  if ((!selectedElement && !index) || selectedElement?.type === 'page')
    return undefined;

  const page =
    pages[index ? index.pageIndex : (selectedElement?.pageIndex ?? 0)];
  if (!page) return undefined;

  const isHeader =
    index?.target === 'header' ||
    (!dynamic && selectedElement && selectedElement.target === 'header');

  const isFooter =
    index?.target === 'footer' ||
    (!dynamic && selectedElement && selectedElement.target === 'footer');

  const sectionArray = isHeader ? header : isFooter ? footer : page.sections;

  const sectionIndex = index
    ? index.sectionIndex
    : (selectedElement?.sectionIndex ?? 0);

  return sectionArray[sectionIndex];
};

export const getTableField = (
  state: TemplateState,
  {
    rowIndex,
    cellIndex,
    fieldIndex,
    nestingLevel = 0,
    parentIndex,
    nestedRowIndex,
  }: {
    pageIndex: number;
    sectionIndex: number;
    rowIndex: number;
    cellIndex: number;
    fieldIndex: number;
    nestingLevel: number;
    parentIndex?: number;
    nestedRowIndex?: number;
  },
): TableField | undefined => {
  const section = selectSections(state);
  const baseRow = section?.rows?.[rowIndex];

  if (nestingLevel === 0) {
    return baseRow?.cells?.[cellIndex]?.fields?.[fieldIndex] as TableField;
  } else if (nestingLevel === 1 && parentIndex !== undefined) {
    const parentCell = baseRow?.cells?.[parentIndex];
    const nestedRow = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
    const nestedCell = nestedRow?.cells?.[cellIndex];
    return nestedCell?.fields?.[fieldIndex] as TableField;
  }

  return undefined;
};

export const getTableCellValue = (
  state: TemplateState,
  payload: SelectedElement & {
    tableRowIndex: number;
    columnIndex: number;
    rowIndex: number;
    cellIndex: number;
    fieldIndex: number;
    sectionIndex: number;
    nestingLevel: number;
    parentIndex?: number;
    nestedRowIndex?: number;
  },
): { cell: TableCell | undefined; row: TableRow | undefined } => {
  const { tableRowIndex, columnIndex } = payload;
  const field = getTableField(state, payload);
  if (field?.type !== 'table' || !field.rows)
    return { cell: undefined, row: undefined };

  const dataRows = field.rows.filter((r) => !r.isHeader);
  const row = dataRows?.[tableRowIndex];
  if (!row || !row.cells[columnIndex])
    return { cell: undefined, row: undefined };

  const allRows = field.rows;
  const targetRowIndex = allRows.findIndex((r) => !r.isHeader && r === row);
  if (targetRowIndex === -1) return { cell: undefined, row: undefined };

  return { cell: field.rows[targetRowIndex].cells[columnIndex], row };
};

export const getFieldCell = (
  state: TemplateState,
  element: {
    pageIndex: number;
    sectionIndex: number;
    rowIndex: number;
    cellIndex: number;
    fieldIndex: number;
    nestingLevel: number;
    parentIndex?: number;
    style: Partial<Field['style']>;
    nestedRowIndex?: number;
    initialMount: boolean;
    target?: TargetType;
  },
): Cell | undefined => {
  const section = selectSections(state, {
    pageIndex: element.pageIndex,
    sectionIndex: element.sectionIndex,
    target: element.target,
  });

  if (!section) return undefined;

  const rowIndex = element.rowIndex;
  const cellIndex = element.cellIndex ?? 0;

  if (element.nestingLevel === 0) {
    return section.rows[rowIndex]?.cells[cellIndex];
  } else {
    const parentCell = section.rows[rowIndex]?.cells[element.parentIndex ?? 0];
    const nestedRow = parentCell?.rows?.[element.nestedRowIndex ?? rowIndex];
    return nestedRow?.cells[cellIndex];
  }
};

export const getFieldRow = (
  state: TemplateState,
  element: {
    pageIndex: number;
    sectionIndex: number;
    rowIndex: number;
    cellIndex: number;
    fieldIndex: number;
    nestingLevel: number;
    parentIndex?: number;
    style: Partial<Field['style']>;
    nestedRowIndex?: number;
    initialMount: boolean;
    target?: TargetType;
  },
): Cell | undefined => {
  const section = selectSections(state, {
    pageIndex: element.pageIndex,
    sectionIndex: element.sectionIndex,
    target: element.target,
  });

  if (!section) return undefined;

  const rowIndex = element.rowIndex;
  const cellIndex = element.cellIndex ?? 0;

  if (element.nestingLevel === 0) {
    return section.rows[rowIndex];
  } else {
    const parentCell = section.rows[rowIndex]?.cells[cellIndex ?? -1];
    return parentCell?.rows?.[element.nestedRowIndex ?? -1];
  }
};

export const isSameElement = (
  a: SelectedElement,
  b: SelectedElement,
): boolean => {
  if (a.type !== b.type) return false;

  switch (a.type) {
    case 'page':
      return a.pageIndex === (b as typeof a).pageIndex;

    case 'section':
      return (
        a.pageIndex === (b as typeof a).pageIndex &&
        a.sectionIndex === (b as typeof a).sectionIndex &&
        a.target === (b as typeof a).target
      );

    case 'row':
      return (
        a.pageIndex === (b as typeof a).pageIndex &&
        a.sectionIndex === (b as typeof a).sectionIndex &&
        a.rowIndex === (b as typeof a).rowIndex &&
        a.nestingLevel === (b as typeof a).nestingLevel &&
        a.parentIndex === (b as typeof a).parentIndex &&
        a.nestedRowIndex === (b as typeof a).nestedRowIndex
      );

    case 'cell':
      return (
        a.pageIndex === (b as typeof a).pageIndex &&
        a.sectionIndex === (b as typeof a).sectionIndex &&
        a.rowIndex === (b as typeof a).rowIndex &&
        a.cellIndex === (b as typeof a).cellIndex &&
        a.nestingLevel === (b as typeof a).nestingLevel &&
        a.parentIndex === (b as typeof a).parentIndex &&
        a.nestedRowIndex === (b as typeof a).nestedRowIndex
      );

    case 'field':
      return (
        a.pageIndex === (b as typeof a).pageIndex &&
        a.sectionIndex === (b as typeof a).sectionIndex &&
        a.rowIndex === (b as typeof a).rowIndex &&
        a.cellIndex === (b as typeof a).cellIndex &&
        a.fieldIndex === (b as typeof a).fieldIndex &&
        a.nestingLevel === (b as typeof a).nestingLevel &&
        a.parentIndex === (b as typeof a).parentIndex &&
        a.nestedRowIndex === (b as typeof a).nestedRowIndex
      );
  }
};
// #endregion

export const isFieldIdDuplicated = (id: string): boolean => {
  const template = useAppSelector(selectSelectedPresent);
  if (!id || !template) return false;

  let count = 0;

  const checkRow = (row: Row): void => {
    for (const cell of row.cells) {
      if (cell.fields) {
        for (const field of cell.fields) {
          if (field.id === id) {
            count++;
            if (count > 1) return;
          }
        }
      }
      if (cell.rows) {
        for (const nestedRow of cell.rows) {
          checkRow(nestedRow);
          if (count > 1) return;
        }
      }
    }
  };

  const checkSection = (section: Section): void => {
    for (const row of section.rows) {
      checkRow(row);
      if (count > 1) return;
    }
  };

  for (const page of template.pages) {
    for (const section of page.sections) {
      checkSection(section);
      if (count > 1) return true;
    }
  }

  if (template.header) {
    for (const section of template.header) {
      checkSection(section);
      if (count > 1) return true;
    }
  }

  if (template.footer) {
    for (const section of template.footer) {
      checkSection(section);
      if (count > 1) return true;
    }
  }

  return false;
};

const isBase64 = (str: string): boolean =>
  typeof str === 'string' && /^data:image\/[a-z]+;base64,/.test(str);

const isBlobUrl = (str: string): boolean =>
  typeof str === 'string' && str.startsWith('blob:');

const blobToBase64 = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const tryConvertToBase64 = async (input: any): Promise<string | undefined> => {
  if (isBase64(input)) return input;

  if (isBlobUrl(input)) {
    try {
      const res = await fetch(input);
      const blob = await res.blob();
      return await blobToBase64(blob);
    } catch (err) {
      console.warn('Failed to convert blob URL to base64', err);
      return '';
    }
  }

  if (input instanceof Blob) {
    return await blobToBase64(input);
  }

  if (input instanceof File) {
    return await fileToBase64(input);
  }

  return '';
};

export const populateTemplateFields = async (
  template: TemplateState,
  data: Record<string, any>,
): Promise<TemplateState> => {
  const clone: TemplateState = structuredClone(template);

  const fillRow = async (row: Row): Promise<void> => {
    // Remove cells that match the empty 'propertyLogo' condition
    row.cells = row.cells.filter((cell) => {
      const originalFieldCount = cell.fields?.length ?? 0;

      // Filter fields
      cell.fields = cell?.fields?.filter((field) => {
        const isPropertyLogoImage =
          field.type === 'image' &&
          field.id === 'propertyLogo' &&
          (field as ImageField).value === imagePlaceholder &&
          (!data?.propertyLogo || data.propertyLogo === '');
        return !isPropertyLogoImage;
      });

      const removedAllFields =
        (cell.fields?.length ?? 0) === 0 && originalFieldCount > 0;

      const hideableID = cell?.hideable ?? '';
      const foundData = data[hideableID];

      if (
        Boolean(
          typeof foundData !== undefined && foundData !== 'true' && hideableID,
        )
      ) {
        return false;
      }

      // Only remove the cell if it previously had fields, and now has none AND has no nested rows
      return !removedAllFields || (cell.rows?.length ?? 0) > 0;
    });

    // Continue with remaining cells
    for (const cell of row.cells) {
      const fieldPromises =
        cell.fields?.map(async (field) => {
          const textKey = getPlaceholders(
            field.type === 'title' ? field.value : '',
          );
          const hasKey = field.type === 'title' && textKey.length > 0;

          const fieldId = field.id;
          const input = fieldId ? data[fieldId] : hasKey;

          if (
            (input === undefined && field.type !== 'table') ||
            (input === false && field.type !== 'table')
          )
            return;

          if (field.type === 'table') {
            const rows = Array.isArray(input) ? input : input?.rows;

            const tableField = field as TableField;

            const defaultHeaderRows =
              tableField?.rows?.filter((r) => r.isHeader === true) ?? [];

            const defaultRows =
              tableField?.rows?.filter((r) => r.isHeader !== true) ?? [];

            if (Array.isArray(rows)) {
              const headerRows = rows.filter((r) => r.isHeader === true);
              const dataRows = rows.filter((r) => !r.isHeader);
              const dataFieldRows = tableField?.rows?.filter(
                (r) => !r.isHeader,
              );

              const finalBodyRows =
                dataFieldRows?.flatMap((row) => {
                  const hasIDTagging = row?.cells?.some((cell) => cell?.id);
                  if (!hasIDTagging) return row;

                  return dataRows.map((dynamicRow) => ({
                    ...row,
                    cells: row.cells.map((templateCell) => {
                      const hasTagging = hasDynamicTag(
                        templateCell?.value ?? '',
                      );

                      if (hasTagging) {
                        const tagMap = Object.fromEntries(
                          dynamicRow.cells.map((c) => [c.id, c.value]),
                        );
                        const newData = injectDynamicData(
                          templateCell.value ?? '',
                          tagMap,
                        );

                        return {
                          ...templateCell,
                          value: newData,
                        };
                      }

                      if (!templateCell.id) return templateCell;

                      const matched = dynamicRow.cells?.find(
                        (item) => item.id === templateCell.id,
                      );
                      return {
                        ...templateCell,
                        value: matched?.value ?? '',
                      };
                    }),
                  }));
                }) ?? [];

              const finalHeaders =
                headerRows.length > 0 ? headerRows : defaultHeaderRows;
              tableField.rows = [...finalHeaders, ...finalBodyRows];
              return;
            }

            const finalBodyRows =
              defaultRows?.flatMap((row) => {
                return {
                  ...row,
                  cells: row.cells.map((templateCell) => {
                    const hasPlaceHolder = getPlaceholders(templateCell?.value);

                    return {
                      ...templateCell,
                      value:
                        injectDynamicData(
                          templateCell.value,
                          hasPlaceHolder.length > 0
                            ? Object.fromEntries(
                                hasPlaceHolder.map((c) => [c, data?.[c] ?? '']),
                              )
                            : {},
                        ) || templateCell.value,
                    };
                  }),
                };
              }) ?? [];

            tableField.rows = [...defaultHeaderRows, ...finalBodyRows];
          } else if (field.type === 'image') {
            const resolved = await tryConvertToBase64(input);
            (field as ImageField).value = resolved || '';
          } else {
            const template = field.value ?? '';
            const fallback = input || '-';

            if (!field.id && !hasDynamicTag(template)) {
              field.value = fallback;
              return;
            }

            field.value = hasDynamicTag(template)
              ? injectDynamicData(
                  template,
                  textKey.length > 0
                    ? Object.fromEntries(
                        textKey.map((c) => [c, data?.[c] ?? '']),
                      )
                    : {
                        [field.id ?? '']: data?.[field.id ?? ''] ?? fallback,
                      },
                )
              : fallback;
          }
        }) ?? [];

      await Promise.all(fieldPromises);

      // Recursively fill nested rows
      if (cell.rows) {
        cell.rows = cell.rows.filter((row) => {
          const hideableID = row?.hideable ?? '';
          const foundData = data[hideableID];

          if (
            typeof foundData !== undefined &&
            foundData !== 'true' &&
            hideableID
          ) {
            return false;
          }

          return true;
        });
        for (const nestedRow of cell.rows) {
          await fillRow(nestedRow);
        }
      }
    }
  };

  const fillSection = async (section: Section) => {
    section.rows = section.rows.filter((row) => {
      const hideableID = row?.hideable ?? '';
      const foundData = data[hideableID];

      if (
        Boolean(
          typeof foundData !== undefined && foundData !== 'true' && hideableID,
        )
      ) {
        return false;
      }

      return true;
    });
    for (const row of section.rows) {
      await fillRow(row);
    }
  };

  for (const page of clone.pages) {
    page.sections = page.sections.filter((row) => {
      const hideableID = row?.hideable ?? '';
      const foundData = data[hideableID];

      if (
        Boolean(
          typeof foundData !== undefined && foundData !== 'true' && hideableID,
        )
      ) {
        return false;
      }

      return true;
    });
    for (const section of page.sections) {
      await fillSection(section);
    }
  }

  if (clone.header) {
    for (const header of clone.header) {
      await fillSection(header);
    }
  }

  if (clone.footer) {
    for (const footer of clone.footer) {
      await fillSection(footer);
    }
  }
  return clone;
};

// RENDERING LIST

export const safeJSONParse = (value?: string | null) => {
  try {
    const sanitized = value?.replace(/[\u200B-\u200F\uFEFF]/g, '').trim();
    return JSON.parse(sanitized ?? '');
  } catch (e) {
    console.warn('Invalid JSON detected:', e);
    return null;
  }
};

export const isBodyContentDetailsArray = (
  arr: any,
): arr is BodyContentDetails[] => {
  return Array.isArray(arr) && arr.every(isBodyContentDetails);
};

const isBodyContentDetails = (obj: any): obj is BodyContentDetails => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    Array.isArray(obj.text) &&
    obj.text.every((t) => typeof t === 'string') &&
    typeof obj.order === 'number' &&
    typeof obj.title === 'string' &&
    typeof obj.textFontWeight === 'number' &&
    typeof obj.titleFontWeight === 'number'
  );
};

const isNonEmptyString = (v: any) =>
  typeof v === 'string' && v.trim() !== '' && v.trim() !== '-';

export const isAllSectionsEmpty = (sections: Section[] = []): boolean => {
  // if no sections -> consider empty
  if (!Array.isArray(sections)) return true;

  return sections.every((section) => {
    if (!section.rows) return true;

    return section.rows.every((row) => {
      if (!row.cells) return true;

      return row.cells.every((cell) => {
        // Check if any field inside the cell is non-empty
        const hasNonEmptyField =
          Array.isArray(cell.fields) &&
          cell.fields.every((field) => {
            // title / textarea
            if (field.type === 'title' || field.type === 'textarea') {
              return isNonEmptyString(field.value);
            }

            // image: non-empty if value is a non-empty string
            if (field.type === 'image') {
              return isNonEmptyString(field.value);
            }

            // table: non-empty only if there exists at least one non-header row
            // with at least one cell that has a non-empty value
            if (field.type === 'table') {
              const rows = field.rows ?? [];

              // find any non-header row (isHeader falsy) with any non-empty cell
              return rows?.length > 1;
            }

            // fallback: treat unknown as empty
            return false;
          });

        // Check nested rows inside the cell (rows of layout)
        const hasNestedRows =
          Array.isArray(cell.rows) &&
          cell.rows.every((nestedRow) =>
            nestedRow.cells?.every(
              (nestedCell) =>
                Array.isArray(nestedCell.fields) &&
                nestedCell.fields.every((f) =>
                  f.type === 'title' ||
                  f.type === 'textarea' ||
                  f.type === 'image'
                    ? isNonEmptyString((f as any).value)
                    : f.type === 'table'
                      ? [...((f as TableField)?.rows ?? [])]?.length > 1
                      : false,
                ),
            ),
          );

        // cell is empty only when there are NO non-empty fields and NO nested rows
        return !hasNonEmptyField && !hasNestedRows;
      });
    });
  });
};

// STYLE
export const pageStylePreset = {
  rowGap: 16,
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: { top: 8, bottom: 8, left: 8, right: 8 },
  fontFamily: 'Roboto',
  border: {
    width: { top: 0, bottom: 0, left: 0, right: 0 },
    style: 'none',
    color: '#CCCCCC',
  },
} as const;

export const sectionStylePreset = {
  width: 100,
  rowGap: 12,
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: { top: 8, bottom: 8, left: 8, right: 8 },
  // backgroundColor: '#FFFFFF',
  border: {
    width: { top: 0, bottom: 0, left: 0, right: 0 },
    style: 'none',
    color: '#CCCCCC',
  },
} as const;

export const rowStylePreset = {
  width: 100,
  columnGap: 8,
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  // backgroundColor: '#FFFFFF',
  border: {
    width: { top: 0, bottom: 0, left: 0, right: 0 },
    style: 'none',
    color: '#CCCCCC',
  },
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
} as const;

export const cellStylePreset = {
  width: 100,
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  // backgroundColor: '#FFFFFF',
  border: {
    width: { top: 0, bottom: 0, left: 0, right: 0 },
    style: 'none',
    color: '#CCCCCC',
  },
  padding: { top: 8, bottom: 8, left: 8, right: 8 },
  gap: 4,
} as const;

export const textStylePreset = {
  width: 100,
  textAlign: 'left',
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
  fontFamily: 'Roboto',
  fontSize: 10,
  fontWeight: 'normal',
  textTransform: 'none',
  textDecoration: 'none',
  fontStyle: 'normal',
  color: '#000000',
  lineHeight: 1.1,
  border: {
    width: { top: 0, bottom: 0, left: 0, right: 0 },
    style: 'none',
    color: '#CCCCCC',
  },
} as const;

export const imageStylePreset = {
  width: 150,
  height: 150,
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  objectFit: 'cover',
  objectPosition: 'center',
} as const;

export const tableStylePreset: NonNullable<TableField['style']> = {
  width: 100,
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  border: {
    width: { top: 0, right: 0, bottom: 0, left: 0 },
    style: 'none',
    color: '#000000',
  },
  gap: 0,
} as const;

export const tableHeadersStylePreset: SharedCellStyle = {
  fontWeight: 'bold',
  textDecoration: 'none',
  padding: { top: 8, right: 4, bottom: 8, left: 4 },
  border: {
    width: { top: 0, right: 0, bottom: 0.5, left: 0 },
    style: 'solid',
    color: '#000000',
  },
  // backgroundColor: '#FFFFFF',
  width: 100,
  textAlign: 'left',
  fontSize: 10,
  fontFamily: 'Roboto',
} as const;

export const tableColumnsStylePreset: SharedCellStyle = {
  fontWeight: 'normal',
  padding: { top: 8, right: 4, bottom: 8, left: 4 },
  border: {
    width: { top: 0.5, right: 0, bottom: 0.5, left: 0 },
    style: 'solid',
    color: '#bfbfbf',
  },
  width: 100,
  textAlign: 'left',
  fontSize: 10,
  fontFamily: 'Roboto',
} as const;
// Style

export const justifyContentOptions = [
  'flex-start',
  'flex-end',
  'center',
  'space-between',
  'space-around',
  'space-evenly',
] as const;

export const alignItemOptions = [
  'flex-start',
  'flex-end',
  'center',
  'baseline',
  'stretch',
] as const;

export const borderStyleOptions = [
  'solid',
  'dashed',
  'dotted',
  'none',
] as const;

export const fontWeightOptions = ['light', 'normal', 'medium', 'bold'] as const;

export const transformOptions = [
  'none',
  'uppercase',
  'lowercase',
  'capitalize',
] as const;

export const decorationOptions = ['none', 'underline', 'line-through'] as const;

export const alignmentOptions: TextAlignmentType[] = [
  'left',
  'center',
  'right',
  'justify',
];

export const flexDirectionOptions = ['row', 'column'];

export const textStyleOptions = ['normal', 'italic'];

export const objectFitOptions = [
  'cover',
  'contain',
  'fill',
  'none',
  'scale-down',
];

export const objectPositionOptions = ['center', 'top', 'left', 'right'];

export const paperSize: PageSize[] = [
  { name: '4A0', width: 1682, height: 2378 },
  { name: '2A0', width: 1189, height: 1682 },
  { name: 'A0', width: 841, height: 1189 },
  { name: 'A1', width: 594, height: 841 },
  { name: 'A2', width: 420, height: 594 },
  { name: 'A3', width: 297, height: 420 },
  { name: 'A4', width: 210, height: 297 },
  { name: 'A5', width: 148, height: 210 },
  { name: 'A6', width: 105, height: 148 },
  { name: 'A7', width: 74, height: 105 },
  { name: 'A8', width: 52, height: 74 },
  { name: 'A9', width: 37, height: 52 },
  { name: 'A10', width: 26, height: 37 },

  { name: 'B0', width: 1000, height: 1414 },
  { name: 'B1', width: 707, height: 1000 },
  { name: 'B2', width: 500, height: 707 },
  { name: 'B3', width: 353, height: 500 },
  { name: 'B4', width: 250, height: 353 },
  { name: 'B5', width: 176, height: 250 },
  { name: 'B6', width: 125, height: 176 },
  { name: 'B7', width: 88, height: 125 },
  { name: 'B8', width: 62, height: 88 },
  { name: 'B9', width: 44, height: 62 },
  { name: 'B10', width: 31, height: 44 },

  { name: 'C0', width: 917, height: 1297 },
  { name: 'C1', width: 648, height: 917 },
  { name: 'C2', width: 458, height: 648 },
  { name: 'C3', width: 324, height: 458 },
  { name: 'C4', width: 229, height: 324 },
  { name: 'C5', width: 162, height: 229 },
  { name: 'C6', width: 114, height: 162 },
  { name: 'C7', width: 81, height: 114 },
  { name: 'C8', width: 57, height: 81 },
  { name: 'C9', width: 40, height: 57 },
  { name: 'C10', width: 28, height: 40 },

  { name: 'RA0', width: 860, height: 1220 },
  { name: 'RA1', width: 610, height: 860 },
  { name: 'RA2', width: 430, height: 610 },
  { name: 'RA3', width: 305, height: 430 },
  { name: 'RA4', width: 215, height: 305 },

  { name: 'SRA0', width: 900, height: 1280 },
  { name: 'SRA1', width: 640, height: 900 },
  { name: 'SRA2', width: 450, height: 640 },
  { name: 'SRA3', width: 320, height: 450 },
  { name: 'SRA4', width: 225, height: 320 },

  { name: 'EXECUTIVE', width: 184, height: 267 }, // 7.25" × 10.5"
  { name: 'FOLIO', width: 210, height: 330 }, // 8.27" × 13"
  { name: 'LEGAL', width: 216, height: 356 }, // 8.5" × 14"
  { name: 'LETTER', width: 216, height: 279 }, // 8.5" × 11"
  { name: 'TABLOID', width: 279, height: 432 }, // 11" × 17"
  { name: 'ID1', width: 85.6, height: 53.98 }, // credit card size
];

// #region IMAGE PLACEHOLDER
export const imagePlaceholder =
  'data:image/jpeg;base64,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';
// #endregion
